const MAX_WIDTH = 1200;
const JPEG_QUALITY = 0.72;
const MAX_OUTPUT_BYTES = Math.floor(1.5 * 1024 * 1024);

/**
 * Redimensiona y comprime una imagen a JPEG para subir en descripciones de tarea.
 * @param {File|Blob} file
 * @returns {Promise<File>}
 */
export async function compressImageForUpload(file) {
    if (!file || !(file.type || '').startsWith('image/')) {
        throw new Error('invalid_type');
    }

    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch (_) {
        bitmap = await loadImageBitmapFallback(file);
    }

    let width = bitmap.naturalWidth || bitmap.width;
    let height = bitmap.naturalHeight || bitmap.height;
    if (!width || !height) {
        bitmap.close?.();
        throw new Error('compress_failed');
    }
    if (width > MAX_WIDTH) {
        height = Math.round((height * MAX_WIDTH) / width);
        width = MAX_WIDTH;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap.close?.();
        throw new Error('compress_failed');
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('compress_failed'))),
            'image/jpeg',
            JPEG_QUALITY
        );
    });

    if (blob.size > MAX_OUTPUT_BYTES) {
        throw new Error('too_large');
    }

    return new File([blob], `image_${Date.now()}.jpg`, { type: 'image/jpeg' });
}

function loadImageBitmapFallback(file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('invalid_type'));
        };
        img.src = url;
    });
}
